import { PackageInitializerUtil } from '@alpha-core/platform-ui';
import { inject, Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { alphaDomains } from '../utils/alpha-service-url-list';
import { AlphaHttpClient } from './alpha-http-client';
import {
    FetchAllPackagesInterface,
    FetchPackageVersionsInterface,
    FetchProjectDependencyInterfaces as FetchProjectDependencyInterface,
    IGlobalCustomCodeDependencyListResponse,
    IListProjectDependenciesListRequest,
    IPackage,
    IProjectDependencyListResponse,
    ListDependency,
    PackageType,
    ProjectDependencyResult,
    ProjectDepsAttribute,
    SnapShotPackages,
    UpdateProjectDependencyInterface,
} from './package-management.interface';
import { ProjectService } from './project.service';
import { TenantService } from './tenant.service';

@Injectable({ providedIn: 'root' })
export class PackageManagementService {
    private http = inject(AlphaHttpClient);
    private projectService = inject(ProjectService);
    private tenantService = inject(TenantService);
    private marketPlaceBasePath = '/marketplace/packages';
    private pageSize = 1000;
    private _cachedPackages!: FetchAllPackagesInterface;
    private _cachedVersions: Map<string, FetchPackageVersionsInterface> = new Map();
    private _packageInitializerUtil!: PackageInitializerUtil;

    set packageInitializerUtil(value: PackageInitializerUtil) {
        this._packageInitializerUtil = value;
    }

    getPackageInitializerUtil(): PackageInitializerUtil {
        return this._packageInitializerUtil;
    }

    async fetchAllPackages(
        packageType: PackageType,
        hasAlphaOwnerShip: boolean = false,
    ): Promise<FetchAllPackagesInterface> {
        if (!this._cachedPackages) {
            const path = this.marketPlaceBasePath + '/metadata';
            this._cachedPackages = await lastValueFrom(
                this.http.post<FetchAllPackagesInterface>(environment.ALPHA_UI_MODULE_SERVICE_URL, path, {
                    packageType,
                    pageSize: this.pageSize,
                    pageNumber: 0,
                }),
            );
        }
        return this._cachedPackages;
    }

    async fetchPackageVersions(
        packageId: string,
        pageNumber: number = 1,
        pageSize: number = 10,
    ): Promise<FetchPackageVersionsInterface> {
        if (!this._cachedVersions.has(packageId)) {
            const path =
                this.marketPlaceBasePath + '/versions/' + packageId + `?pageNumber=${pageNumber}&pageSize=${pageSize}`;
            const response = await lastValueFrom(
                this.http.get<FetchPackageVersionsInterface>(environment.ALPHA_UI_MODULE_SERVICE_URL, path),
            );
            this._cachedVersions.set(`${packageId}-${pageNumber}-${pageSize}`, response);
        }
        return this._cachedVersions.get(`${packageId}-${pageNumber}-${pageSize}`) as FetchPackageVersionsInterface;
    }

    async fetchProjectPackages(
        packageType: PackageType[] = [],
        pageSize: number,
        pageNumber: Number,
    ): Promise<FetchProjectDependencyInterface> {
        const path = '/project-dependency/fetch-all';
        const project = this.projectService.fetchCurrentProject();
        const tag = await this.projectService.getLatestProjectTag(project.projectId);
        return await lastValueFrom(
            this.http.post(environment.ALPHA_UI_CONFIG_SERVICE_URL, path, {
                projectId: project.projectId,
                tenantId: this.tenantService.fetchTenant(),
                projectTagVersion: tag?.name,
                packageType,
                pageSize,
                pageNumber,
            }),
        );
    }

    fetchProjectDependencyList(
        listConfig: IListProjectDependenciesListRequest,
    ): Promise<IProjectDependencyListResponse> {
        const path = '/project-dependency-config/list';
        const project = this.projectService.fetchCurrentProject();
        const payload = {
            projectId: project.projectId,
            ...(listConfig.packageSearch ? { packageSearch: listConfig.packageSearch } : {}),
            ...(listConfig.pageNumber ? { pageNumber: listConfig.pageNumber } : {}),
            ...(listConfig.pageSize ? { pageSize: listConfig.pageSize } : {}),
        };
        return lastValueFrom(
            this.http.post<IProjectDependencyListResponse>(alphaDomains.config_service_url, path, payload),
        );
    }
    updateProjectDependency(configId: string, dependency: IPackage): Promise<ProjectDependencyResult> {
        const path = '/project-dependency-config/update';
        const project = this.projectService.fetchCurrentProject();
        const tenantId = this.tenantService.fetchTenant();
        const payload = {
            projectId: project.projectId,
            tenantId,
            configId,
            dependencyConfig: dependency,
        };
        return lastValueFrom(this.http.post<ProjectDependencyResult>(alphaDomains.config_service_url, path, payload));
    }

    fetchGlobalCustomCodetDependencyList(payload: ListDependency): Promise<IGlobalCustomCodeDependencyListResponse> {
        const tenantId = this.tenantService.fetchTenant();
        const path = '/global-custom-code/dependency/fetch-all';
        return lastValueFrom(
            this.http.post<IGlobalCustomCodeDependencyListResponse>(alphaDomains.config_service_url, path, {
                ...payload,
                tenantId,
            }),
        );
    }

    updateGlobalCustomCodeDependency(
        configId: string,
        prevConfigVersion: string,
        updatedPackages: Array<Partial<SnapShotPackages>>,
    ) {
        const path = '/global-custom-code/dependency/update';
        const payload = {
            configId,
            configVersionId: prevConfigVersion,
            updatedPackages,
            tenantId: this.tenantService.fetchTenant(),
        };
        return lastValueFrom(this.http.post(alphaDomains.config_service_url, path, payload));
    }

    async updatePackages(
        configId: string,
        attributes: ProjectDepsAttribute[],
    ): Promise<UpdateProjectDependencyInterface> {
        const path = '/project-dependency/update';
        return await lastValueFrom(
            this.http.post(environment.ALPHA_UI_CONFIG_SERVICE_URL, path, {
                configId,
                attributes,
            }),
        );
    }

    addDefaultDependencyList() {
        const path = '/project-dependency-config/initialize';
        const project = this.projectService.fetchCurrentProject();
        const tenantId = this.tenantService.fetchTenant();
        const payload = {
            projectId: project.projectId,
            tenantId,
        };
        return lastValueFrom(this.http.post(alphaDomains.config_service_url, path, payload));
    }
}
